import { nanoid } from 'nanoid';

export interface BaseRecord {
  id?: string;
  created_at?: string;
  updated_at?: string;
}

export class BaseStorage<T extends BaseRecord> {
  constructor(private storageKey: string) {}

  protected getAll(): T[] {
    const stored = localStorage.getItem(this.storageKey);
    return stored ? JSON.parse(stored) : [];
  }

  protected save(items: T[]): void {
    localStorage.setItem(this.storageKey, JSON.stringify(items));
  }

  get(): T[] {
    return this.getAll();
  }

  create(item: T): T {
    const items = this.getAll();
    const newItem = {
      ...item,
      id: nanoid(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    items.unshift(newItem);
    this.save(items);
    return newItem;
  }

  update(id: string, updates: Partial<T>): T {
    const items = this.getAll();
    const index = items.findIndex(item => item.id === id);
    
    if (index === -1) {
      throw new Error(`Item with id ${id} not found`);
    }

    const updatedItem = {
      ...items[index],
      ...updates,
      updated_at: new Date().toISOString()
    };

    items[index] = updatedItem;
    this.save(items);
    return updatedItem;
  }

  delete(id: string): void {
    const items = this.getAll().filter(item => item.id !== id);
    this.save(items);
  }

  clear(): void {
    this.save([]);
  }
}